import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Phone, Calendar, MapPin, ArrowUpRight } from "lucide-react";
import { business } from "../data/business";
import { useBusinessPhone } from "../hooks/useBusinessPhone";
import { fireToast } from "./Toast";

const mapsUrl = `https://maps.google.com/?q=${encodeURIComponent(business.address.full)}`;

// Mobile-only sticky bar — Call · Book · Map.
// item 37: only appears once the user scrolls past the hero so it doesn't compete with hero CTAs.
// "Trips" was removed: it duplicated the header nav and the Book CTA already
// covers the conversion intent. Three slots reads cleaner on small screens.
//
// Round 2 item #8 (Agent B): we keep the dock rendered on /contact and
// /legal/* too. Rationale: even when a visitor is on a contact form the
// Book CTA remains the primary conversion path, and the Call shortcut is
// still useful (most users land on /contact precisely to reach us). The
// keyboard-aware hide (#12 below) ensures the dock doesn't sit on top of
// an open virtual keyboard while a form input is focused, so it never
// gets in the way of typing on /contact.
export function MobileBottomBar() {
  const { phone, phoneRaw } = useBusinessPhone();
  const [visible, setVisible] = useState(false);
  // Round 2 item #12 (Agent B): hide the dock while the virtual keyboard
  // is up. Detected via window.visualViewport — when the visual viewport
  // shrinks more than 20% relative to layout viewport, we assume a soft
  // keyboard. Feature-detected; falls back to "no hiding" on browsers
  // without visualViewport (older Safari, some embedded WebViews).
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const location = useLocation();

  // Mobile-feedback round (item #21): scroll-direction aware visibility.
  // Previously the dock appeared at scrollY > 480 and stayed visible until
  // the page top. That competes with content while reading long-form
  // copy (BBQ menu, FAQ, policy pages). New behavior:
  //   • Stay HIDDEN until scrollY > 480 (initial reveal threshold unchanged)
  //   • After the reveal, HIDE when the user scrolls DOWN by >100px from
  //     the last "show" position (intent to read, not to act)
  //   • SHOW again when the user scrolls UP by >40px from the last
  //     "hide" position (intent to take action — Call/Book/Map are
  //     within reach)
  //   • At the very top of the page the dock disappears regardless of
  //     direction, since the header's BOOK pill is already visible
  useEffect(() => {
    let raf = 0;
    let lastY = window.scrollY;
    let lastDirChangeY = lastY;
    let dir: "up" | "down" = "down";
    let shown = false;

    const REVEAL_THRESHOLD = 480;
    const HIDE_AFTER = 100;
    const SHOW_AFTER = 40;

    const tick = () => {
      raf = 0;
      const y = window.scrollY;

      // Above the initial reveal threshold → always hidden.
      if (y < REVEAL_THRESHOLD) {
        shown = false;
        setVisible(false);
        lastY = y;
        return;
      }

      const newDir: "up" | "down" = y > lastY ? "down" : y < lastY ? "up" : dir;
      if (newDir !== dir) {
        // Direction flipped — reset the "distance since last flip" anchor.
        dir = newDir;
        lastDirChangeY = lastY;
      }
      const delta = Math.abs(y - lastDirChangeY);

      if (dir === "down" && shown && delta > HIDE_AFTER) {
        shown = false;
        setVisible(false);
      } else if (dir === "up" && !shown && delta > SHOW_AFTER) {
        shown = true;
        setVisible(true);
      } else if (!shown && dir === "down" && y > REVEAL_THRESHOLD && lastY < REVEAL_THRESHOLD) {
        // First time crossing the reveal threshold while scrolling down —
        // show once so the user discovers the dock exists.
        shown = true;
        setVisible(true);
      } else if (!shown && y === lastY) {
        // No scroll delta and dock hidden — keep hidden.
        // (no-op; prevents spurious flicker on layout shifts)
      }

      lastY = y;
    };

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(tick);
    };
    // Force one tick so the dock appears immediately if the page loaded
    // already scrolled past the reveal threshold (e.g., back navigation).
    tick();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Round 2 item #12: visualViewport resize → infer keyboard state. We
  // compare visualViewport.height against window.innerHeight; a >20%
  // shrink is the canonical heuristic for a soft keyboard being up on
  // both iOS Safari and Android Chrome.
  useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!vv) return;
    const check = () => {
      const layoutH = window.innerHeight;
      const visualH = vv.height;
      // Guard against zero/NaN before innerHeight is ready.
      if (!layoutH) return;
      const shrunk = (layoutH - visualH) / layoutH > 0.2;
      setKeyboardOpen(shrunk);
    };
    check();
    vv.addEventListener("resize", check);
    vv.addEventListener("scroll", check);
    return () => {
      vv.removeEventListener("resize", check);
      vv.removeEventListener("scroll", check);
    };
  }, []);

  // Round 2 item #11 (Agent B): 10ms haptic on every dock tap (Call, Book,
  // Map). Android Chrome honors `navigator.vibrate`; iOS Safari ignores it,
  // so this is a progressive enhancement. Wrapped because some browsers
  // throw on the call under strict autoplay/secure-context rules.
  const buzz = () => {
    try {
      navigator.vibrate?.(10);
    } catch {
      /* no-op — vibration is a nice-to-have */
    }
  };

  // Round 2 item #9: active state. Compare the current pathname to each
  // dock item's anchor. Book and Map are external so they never match;
  // Call (tel:) is also non-route. The only route we surface here is the
  // implicit "current page" — applied to nothing today, but the structure
  // is in place if we add an internal-route dock item later. We DO mark
  // the entire bar with a data attribute so CSS / analytics can read the
  // current route if needed.
  const pathname = location.pathname;

  return (
    <div
      className={`m-bar ${visible ? "is-visible" : ""} ${keyboardOpen ? "is-keyboard-open" : ""}`}
      role="navigation"
      aria-label="Quick actions"
      data-route={pathname}
    >
      <a
        href={`tel:${phoneRaw}`}
        className="m-bar-item"
        aria-label={`Call ${phone}`}
        onClick={buzz}
      >
        <Phone aria-hidden="true" /> <span className="m-bar-label">Call</span>
      </a>
      <a
        href={business.bookingBase}
        target="_blank"
        rel="noopener noreferrer"
        className="m-bar-item m-bar-item-primary"
        aria-label="Book now (opens in a new tab)"
        title="Opens booking site in a new tab"
        onClick={() => { buzz(); fireToast("Opening booking page…"); }}
      >
        <Calendar aria-hidden="true" /> <span className="m-bar-label">Book Now</span>
        {/* item #58 — sr-only hint and item #11 — external arrow icon */}
        <span className="sr-only">Opens in a new tab</span>
        <ArrowUpRight aria-hidden="true" className="ext-arrow" />
      </a>
      <a
        href={mapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="m-bar-item"
        aria-label="Open map directions (opens in a new tab)"
        onClick={() => { buzz(); fireToast("Opening directions…"); }}
      >
        <MapPin aria-hidden="true" /> <span className="m-bar-label">Map</span>
        <span className="sr-only">Opens in a new tab</span>
      </a>
    </div>
  );
}
